import { ILocation } from '@models/interfaces/ILocation';
import { TranslateService } from '@ngx-translate/core';
import { xml2json } from '@techwan/mapping';
import { ILocationFormatter } from '../ILocationFormatter';

const FIELDS_TO_SHOW: string[] = [
  'Area3Name',
  'AliasName',
  'PlaceName',
  'StreetNumber',
  'CATEGORIE',
  'REFHIGHWAYS_SNAME',
  'REFTRACKS_SNAME',
  'REFNODES_SNAME',
  'SFULLTEXT'
];

export class DefaultLocationFormatter implements ILocationFormatter {
  constructor(protected translate: TranslateService) {}

  format(location: ILocation): string[] {
    const jsonMiscData = this.parseMiscData(location.LocationMiscData);

    const digiCode: string = this.getDigiCode(jsonMiscData);
    const kmPoint: string = this.getKmPoint(jsonMiscData);
    const renderedFields: string = this.renderFields(FIELDS_TO_SHOW, location, jsonMiscData);
    const formattedLocation: string = [renderedFields, kmPoint, digiCode].filter(s => s).join(', ');

    return formattedLocation ? [formattedLocation] : [];
  }

  protected parseMiscData(miscData: string): any {
    let jsonMiscData = {};

    if (miscData) {
      try {
        jsonMiscData = JSON.parse(this.isXml(miscData) ? this.xml2JsonString(miscData) : miscData);
      } catch (e) {
        console.error(e);
      }
    }

    return jsonMiscData;
  }

  private isXml(miscData: string) {
    return miscData && miscData.startsWith('<');
  }

  private xml2JsonString(xml: string): string {
    const xmlDoc: Document = new DOMParser().parseFromString(xml, 'text/xml');
    return xml2json(xmlDoc, '  ');
  }

  protected getDigiCode(jsonMiscData: any): string {
    const digiCode = this.getAttributeValue('Digicode', jsonMiscData);
    return digiCode ? `${this.translate.instant('Mobile.DigiCode')}: ${digiCode}` : null;
  }

  protected getAttributeValue(fieldName: string, jsonMiscData): any {
    return jsonMiscData['attributes'] ? jsonMiscData['attributes'][fieldName] : undefined;
  }

  protected getKmPoint(jsonMiscData, fieldName = 'PK') {
    const kmPoint = this.getAttributeValue(fieldName, jsonMiscData);
    return kmPoint ? `${this.translate.instant('Mobile.PK')}${this.getFormattedKilometerPoint(kmPoint)}` : '';
  }

  private getFormattedKilometerPoint(kmPoint: number | string) {
    const kilometerPoint: number = Number(kmPoint);
    const formattedKmPoint = kilometerPoint >= 1000 ? kilometerPoint / 1000 : kilometerPoint;
    return `${formattedKmPoint
      .toFixed(1)
      .toString()
      .replace('.', ',')} km`;
  }

  protected renderFields(fields: string[], location: ILocation, jsonMiscData: any): string {
    return fields
      .map(fieldName => this.getFieldValue(fieldName, location, jsonMiscData))
      .filter(s => s)
      .join(', ');
  }

  private getFieldValue(fieldName: string, location: ILocation, jsonMiscData: any): any {
    return location[fieldName] || this.getAttributeValue(fieldName, jsonMiscData);
  }
}
